/**
 * CCFI fencing catalogue constants — Colorbond palette + gate templates.
 * Deliberately hardcoded (short, stable list). Add-on rates and new items are
 * managed via the admin Pricing page which is backed by MongoDB.
 */

// The Standard 8 Colorbond® colours we sell most of, plus raw galvanised
// finish for un-painted steel gates / structural work.
export const COLORBOND_COLOURS = [
  { name: "Woodland Grey",  hex: "#4B5157" },
  { name: "Monument",       hex: "#333638" },
  { name: "Basalt",         hex: "#585A5B" },
  { name: "Surfmist",       hex: "#E5E1D8" },
  { name: "Classic Cream",  hex: "#E9D9B5" },
  { name: "Windspray",      hex: "#7A8082" },
  { name: "Cove",           hex: "#6E7D82" },
  { name: "Ironstone",      hex: "#40474D" },
  { name: "Gal",            hex: "#B7BCC0", finish: "gal" },
];

// Standard gate templates — id must match a pricing_entries `gate_template_id`.
export const GATE_TEMPLATES = [
  { id: "ped_1m",     label: "1.0 m pedestrian",           default_width_m: 1.0 },
  { id: "ped_1_2m",   label: "1.2 m pedestrian (DDA)",     default_width_m: 1.2 },
  { id: "swing_3m",   label: "3.0 m single-swing",         default_width_m: 3.0 },
  { id: "swing_4m",   label: "4.0 m single-swing",         default_width_m: 4.0 },
  { id: "double_7m",  label: "7.0 m double-swing",         default_width_m: 7.0 },
  { id: "double_8m",  label: "8.0 m double-swing",         default_width_m: 8.0 },
  { id: "slide_6m",   label: "6.0 m sliding",              default_width_m: 6.0 },
  { id: "other",      label: "Other / custom gate",        default_width_m: 3.0 },
];

// Standard heights we quote in mm, per fence type.
export const HEIGHT_OPTIONS = {
  colorbond:    [1500, 1800, 2100],
  corromesh:    [1500, 1800, 2100, 2400],
  chain_wire:   [1200, 1500, 1800, 2100, 2400],
  welded_mesh:  [1800, 2100, 2400, 3000],
  tubular:      [1200, 1500, 1800],
  slat:         [1200, 1500, 1800, 2100],
  slat_panel:   [1200, 1500, 1800, 2100],
  pool_alu:     [1200, 1300],
  fence_extension: [300, 450, 600, 900],
  custom_build: [1200, 1500, 1800, 2100, 2400],
  steel_paling: [1500, 1800, 2100],
  post_rail:    [1200, 1500, 1800],
  other:        [1200, 1500, 1800, 2100, 2400],
};

// Map fence_type → colour palette shown next to it. Corromesh is Colorbond
// corrugated sheet so it uses the same colour range.
export const HAS_COLOUR_PICKER = new Set(["colorbond", "corromesh"]);

// Gate infill styles Trent offers (independent of the gate width template).
export const GATE_STYLES = [
  { id: "colorbond",      label: "Colorbond infill" },
  { id: "chainwire",      label: "Chainwire" },
  { id: "glass",          label: "Glass" },
  { id: "aluminium_slat", label: "Aluminium slats" },
  { id: "tubular",        label: "Tubular / spear-top" },
  { id: "welded_mesh",    label: "Welded mesh" },
  { id: "custom",         label: "Custom / other" },
];

// Auto material take-off for a quote section (posts + concrete @ 2 bags/post).
export function calcSectionMaterials(s) {
  if (s.section_type === "gate") {
    const qty = Math.max(1, parseInt(s.qty, 10) || 1);
    const perGate = s.gate_posts === undefined || s.gate_posts === null ? 2 : Math.max(0, parseInt(s.gate_posts, 10) || 0);
    const posts = perGate * qty;
    return { isGate: true, qty, perGate, posts, concreteBags: posts * 2 };
  }
  if (s.section_type === "fence") {
    const L = parseFloat(s.length_m) || 0;
    if (L <= 0) return null;
    const panels = Math.max(1, Math.ceil(L / 2.4));
    const posts = panels + 1;
    return { isGate: false, panels, posts, concreteBags: posts * 2 };
  }
  return null;
}

// Map a Material Master unit → a quote-section unit.
export function materialUnitToSection(u) {
  switch ((u || "").toLowerCase()) {
    case "lm": return "LM";
    case "sqm": return "m2";
    case "hr": return "hour";
    case "day": return "day";
    case "ea":
    default: return "each";
  }
}
